import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, RefreshCw, ArrowUpRight, ArrowRight, Sparkles,
  Wallet, Truck, CheckCircle2, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DossierForm from "@/components/dossier/DossierForm";
import PaymentActionSheet from "@/components/dossier/PaymentActionSheet";
import DeliverySlotsSheet from "@/components/dossier/DeliverySlotsSheet";
import {
  Button, IconButton, SegmentedControl, Toolbar,
  Chip, Avatar, VehicleThumb,
} from "@/components/wyvio-ui";

/**
 * Centre de pilotage · Sprint V2 (v3 · passe finition)
 * ─────────────────────────────────────────────────────
 * Objectifs :
 *   1. Respiration : sections espacées de 32-40 px de plus, aucun séparateur.
 *   2. KPI storytelling : chaque tuile répond à une question métier.
 *   3. Cartes "À traiter" véhiculo-centrées : photo, action, en 2 secondes.
 *   4. Interface silencieuse : les H2 disparaissent, seuls les micros restent.
 *   5. ADN automobile : photo véhicule partout, placeholder wyvio si absent.
 *   6. Micro-interactions sub 200 ms, jamais démonstratives.
 */

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────
const firstName = (full) => (full || "").split(" ")[0] || "";
const numberFR = (n) => Number(n || 0).toLocaleString("fr-FR");
const fmtDay = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      weekday: "short", day: "numeric",
    }).replace(/\./g, "");
  } catch { return null; }
};

// VehicleThumb, Avatar, Chip, MicroLabel, Card, Toolbar, Button viennent tous
// de wyvio-ui — aucune inline redéfinition.

// KpiCard reste inline pour l'instant : sémantique très spécifique (chiffre
// hero + storytelling). Il sera extrait quand une 2ᵉ page en aura besoin.

// ────────────────────────────────────────────────────────────────────────
// KPI Card v3 — storytelling
// ────────────────────────────────────────────────────────────────────────
// Layout : micro-label ← flèche · chiffre géant · phrase courte descriptive.
// Aucun texte inutile, aucune décoration.
// ────────────────────────────────────────────────────────────────────────
const KpiCard = ({ testId, micro, primary, secondary, muted = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={[
      "group relative w-full text-left rounded-3xl px-8 pt-7 pb-7 h-full",
      "bg-white dark:bg-[#111111]",
      "border border-[#EEF2F5] dark:border-[#262626]",
      "transition-all duration-200 ease-out",
      "hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
      "hover:-translate-y-[1px]",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8FF2F]",
    ].join(" ")}
  >
    <div className="flex items-start justify-between">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9CA3AF] dark:text-[#525252]">
        {micro}
      </p>
      <ArrowUpRight
        size={14}
        strokeWidth={2}
        className="text-[#D1D5DB] dark:text-[#3A3A3A] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
      />
    </div>
    <div className="mt-8">
      <div className={[
        "text-[44px] sm:text-[52px] leading-[0.95] font-semibold tracking-[-0.04em] tabular-nums",
        muted ? "text-[#D1D5DB] dark:text-[#3F3F3F]" : "text-[#0A0A0A] dark:text-white",
      ].join(" ")}>
        {primary}
      </div>
      {secondary && (
        <div className={[
          "mt-3 text-[12.5px] leading-snug",
          muted
            ? "text-[#9CA3AF] dark:text-[#525252]"
            : "text-[#6B7280] dark:text-[#9CA3AF]",
        ].join(" ")}>
          {secondary}
        </div>
      )}
    </div>
  </button>
);

// ────────────────────────────────────────────────────────────────────────
// QuickAction — décide du sheet à ouvrir d'après le cta.action fourni
// par l'API command-center (`open_payment`, `open_delivery`, etc.).
// Le but est de traiter l'action en 1 clic depuis le dashboard.
// ────────────────────────────────────────────────────────────────────────
const quickActionForItem = (item) => {
  const action = item?.cta?.action || item?.cta_action;
  if (action === "open_payment") {
    return { kind: "payment", label: "Paiement reçu", icon: Wallet };
  }
  if (action === "open_delivery") {
    return { kind: "delivery", label: "Créneaux", icon: Truck };
  }
  // Pas de quick action — la carte ouvre le dossier normalement.
  return null;
};

const QuickActionPill = ({ item, onClick, testId }) => {
  const qa = quickActionForItem(item);
  if (!qa) return null;
  const Icon = qa.icon;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(qa); }}
      data-testid={testId}
      title={qa.label}
      className="hidden sm:inline-flex h-9 px-3 items-center gap-1.5 rounded-full bg-[#C8FF2F] text-[#0A0A0A] text-[12px] font-semibold hover:brightness-95 active:scale-[0.97] transition-all shadow-sm"
    >
      <Icon size={12} strokeWidth={2.5} />
      {qa.label}
      <ArrowRight size={11} strokeWidth={2.5} className="opacity-70" />
    </button>
  );
};
// Layout compact : [photo] · titre + client · action | flèche
// - Photo véhicule prioritaire (ADN)
// - CTA fournie par l'API (`item.cta`) devient chip vert wyvio
// - Client en avatar initiales à droite
// ────────────────────────────────────────────────────────────────────────
const PriorityRow = ({ item, onOpen, onQuickAction, testId }) => (
  <div
    data-testid={testId}
    className="group w-full flex items-center gap-5 py-4 pl-4 pr-6 rounded-2xl text-left
               bg-white dark:bg-[#111111]
               border border-[#EEF2F5] dark:border-[#262626]
               hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]
               transition-all duration-200"
  >
    <button
      onClick={onOpen}
      data-testid={`${testId}-open`}
      className="flex-1 min-w-0 flex items-center gap-5 text-left"
      aria-label={`Ouvrir ${item.vehicle_name || "le dossier"}`}
    >
      <VehicleThumb src={item.vehicle_image} alt={item.vehicle_name} size={64} />

      <div className="flex-1 min-w-0">
        <p className="text-[15.5px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.01em] truncate">
          {item.vehicle_name || "Dossier"}
        </p>
        <p className="mt-1 text-[12px] text-[#9CA3AF] dark:text-[#737373] truncate">
          {item.top_reason_label || "Action requise"}
        </p>
      </div>
    </button>

    {/* Quick action inline (Sprint Dashboard Express) */}
    <QuickActionPill
      item={item}
      onClick={(qa) => onQuickAction(item, qa)}
      testId={`${testId}-quick`}
    />

    {/* Fallback : simple chip informatif si pas de quick action */}
    {!quickActionForItem(item) && item.cta && (
      <Chip
        tone="accent"
        className="hidden sm:inline-flex"
        data-testid={`${testId}-cta`}
      >
        {typeof item.cta === "string" ? item.cta : (item.cta.label || "Ouvrir")}
      </Chip>
    )}

    <div className="hidden sm:block">
      <Avatar name={item.client_name} size={8} />
    </div>

    <button
      onClick={onOpen}
      aria-label="Ouvrir le dossier"
      data-testid={`${testId}-arrow`}
      className="p-1 -mr-1"
    >
      <ArrowRight
        size={14}
        strokeWidth={2}
        className="text-[#D1D5DB] dark:text-[#3A3A3A] shrink-0 group-hover:translate-x-1 group-hover:text-[#0A0A0A] dark:group-hover:text-white transition-all duration-200"
      />
    </button>
  </div>
);

// ────────────────────────────────────────────────────────────────────────
// Delivery row v3
// ────────────────────────────────────────────────────────────────────────
const DeliveryRow = ({ item, onOpen, testId }) => {
  const day = fmtDay(item.delivery_date_iso || item.delivery_date);
  const isToday = item.days_to_delivery === 0;
  return (
    <button
      onClick={onOpen}
      data-testid={testId}
      className="group w-full flex items-center gap-5 py-3.5 pl-4 pr-6 rounded-2xl text-left
                 bg-white dark:bg-[#111111]
                 border border-[#EEF2F5] dark:border-[#262626]
                 hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]
                 transition-all duration-200"
    >
      <VehicleThumb src={item.vehicle_image} alt={item.vehicle_name} size={56} />

      <div className="w-20 shrink-0">
        <p className={[
          "text-[9.5px] font-semibold uppercase tracking-[0.14em]",
          isToday ? "text-[#0A0A0A] dark:text-white" : "text-[#9CA3AF] dark:text-[#737373]",
        ].join(" ")}>
          {isToday ? "Aujourd'hui" : day || "—"}
        </p>
        {item.delivery_time && (
          <p className="mt-0.5 text-[14px] font-semibold text-[#0A0A0A] dark:text-white tabular-nums tracking-tight">
            {item.delivery_time}
          </p>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.01em] truncate">
          {item.vehicle_name || "Livraison"}
        </p>
        {item.client_name && (
          <p className="mt-0.5 text-[12px] text-[#9CA3AF] dark:text-[#737373] truncate">
            {item.client_name}
          </p>
        )}
      </div>

      {item.readiness === "blocked" ? (
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#0A0A0A] dark:text-white bg-[#F3F4F6] dark:bg-[#1A1A1A] px-2.5 py-1 rounded-full shrink-0">
          À débloquer
        </span>
      ) : (
        <span className="h-2 w-2 rounded-full bg-[#C8FF2F] shrink-0" aria-hidden />
      )}
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Main
// ────────────────────────────────────────────────────────────────────────
const CommandCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const isAdminRole = ["admin", "agency_owner", "platform_admin"].includes(user?.role);
  const [scope, setScope] = useState(isAdminRole ? "team" : "mine");
  const [showForm, setShowForm] = useState(false);
  // Sprint · Dashboard Express — sheets inline
  const [quickSheet, setQuickSheet] = useState(null); // {kind, dossierId, paymentSummary?, deliveryCurrent?}
  const [quickLoading, setQuickLoading] = useState(false);

  const openQuickAction = async (item, qa) => {
    setQuickLoading(true);
    try {
      if (qa.kind === "payment") {
        const p = await api.getPayment(item.id).catch(() => null);
        setQuickSheet({ kind: "payment", dossierId: item.id, paymentSummary: p?.summary });
      } else if (qa.kind === "delivery") {
        const r = await api.getDelivery(item.id).catch(() => null);
        const dr = await api.getDossier(item.id).catch(() => null);
        setQuickSheet({
          kind: "delivery",
          dossierId: item.id,
          deliveryCurrent: r?.delivery,
          defaultLocation: r?.delivery?.location || dr?.delivery_location || "",
        });
      }
    } finally {
      setQuickLoading(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const d = await api.getCommandCenter({ scope });
      setData(d); setError(null);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [scope]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(); };
  const openDossier = (id) => navigate(`/app/dossier/${id}`);

  const priorityList = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const src of [data?.my_actions || [], data?.at_risk_dossiers || []]) {
      for (const item of src) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        out.push(item);
        if (out.length >= 5) break;
      }
      if (out.length >= 5) break;
    }
    return out;
  }, [data]);

  const deliveries = useMemo(() => (data?.delivery_readiness || []).slice(0, 6), [data]);

  const canViewTeam = data?.user?.can_view_all;

  // Loading state (skeleton silencieux)
  if (loading && !data) {
    return (
      <div
        className="-mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-screen bg-[#F7F8FA] dark:bg-[#080808] transition-colors"
        data-testid="page-command-center-loading"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-32">
          <div className="animate-pulse space-y-14">
            <div className="h-14 w-96 max-w-full bg-[#EEF2F5] dark:bg-[#1A1A1A] rounded-2xl" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-44 bg-white dark:bg-[#111111] border border-[#EEF2F5] dark:border-[#262626] rounded-3xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="-mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-screen bg-[#F7F8FA] dark:bg-[#080808] flex items-center justify-center transition-colors" data-testid="page-command-center-error">
        <div className="text-center">
          <p className="text-[13px] text-[#991B1B]">{error}</p>
          <button onClick={load} className="mt-4 h-9 rounded-full bg-[#0A0A0A] dark:bg-white text-white dark:text-[#0A0A0A] text-[12.5px] px-4">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const ov = data.overview;
  const attentionCount = (ov.blocked || 0) + (ov.payments_awaiting || 0)
    + (ov.deliveries_today || 0) + (ov.unread_messages || 0);
  const priorityTop = priorityList[0];
  const primaryDelivery = deliveries.find((d) => d.days_to_delivery === 0)
    || deliveries[0];
  const attentionLabel = attentionCount === 0
    ? "Aucune action ne vous attend aujourd'hui."
    : attentionCount === 1
    ? "1 action demande votre attention."
    : `${numberFR(attentionCount)} actions demandent votre attention.`;

  // Storytelling des sous-textes KPI
  const paymentsHint = ov.payments_awaiting
    ? "à encaisser"
    : "tout est à jour";
  const messagesHint = ov.unread_messages
    ? "en attente de réponse"
    : "boîte à jour";
  const deliveriesPrimary = primaryDelivery && primaryDelivery.days_to_delivery === 0
    ? (primaryDelivery.delivery_time || "Aujourd'hui")
    : (ov.deliveries_today || 0);
  const deliveriesHint = primaryDelivery
    ? primaryDelivery.vehicle_name
    : "rien de prévu";
  const blockedPrimary = ov.blocked || 0;
  const blockedHint = priorityTop?.top_reason_label
    ? priorityTop.top_reason_label.toLowerCase()
    : ov.blocked
    ? "à débloquer"
    : "aucun blocage";

  return (
    <div
      className="-mx-6 lg:-mx-10 -my-8 lg:-my-12 min-h-[calc(100vh-0px)] bg-[#F7F8FA] dark:bg-[#080808] transition-colors"
      data-testid="page-command-center"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-32">

        {/* ─── TOOLBAR ─── */}
        <Toolbar
          className="mb-20"
          left={
            canViewTeam ? (
              <SegmentedControl
                testId="scope-switch"
                value={scope}
                onChange={setScope}
                options={[
                  { key: "mine", label: "Moi" },
                  { key: "team", label: "Agence" },
                ]}
              />
            ) : <span />
          }
          right={
            <>
              <IconButton
                icon={RefreshCw}
                loading={refreshing}
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="cc-refresh"
                title="Rafraîchir"
              />
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => setShowForm(true)}
                data-testid="cc-new-dossier"
              >
                Nouveau dossier
              </Button>
            </>
          }
        />

        {/* ─── HERO — salut + promesse ─── */}
        <section data-testid="cc-hero" className="mb-20">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-[#9CA3AF] dark:text-[#525252]">
            {new Date(data.today).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1
            data-testid="cc-title"
            className="mt-5 text-[42px] sm:text-[58px] lg:text-[72px] leading-[0.98] font-semibold tracking-[-0.045em] text-[#0A0A0A] dark:text-white"
          >
            Bonjour {firstName(user?.name) || "Stéphane"}.
          </h1>
          <p
            data-testid="cc-attention-line"
            className="mt-5 text-[19px] sm:text-[22px] leading-[1.35] text-[#6B7280] dark:text-[#A3A3A3] max-w-2xl"
          >
            {attentionLabel}
          </p>
        </section>

        {/* ─── 4 KPI ─── */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          data-testid="today-overview"
        >
          <KpiCard
            testId="kpi-payments"
            micro="Paiements"
            primary={numberFR(ov.payments_awaiting || 0)}
            secondary={paymentsHint}
            muted={!ov.payments_awaiting}
            onClick={() => navigate("/app/dossiers?focus=payment")}
          />
          <KpiCard
            testId="kpi-messages"
            micro="Messages"
            primary={numberFR(ov.unread_messages || 0)}
            secondary={messagesHint}
            muted={!ov.unread_messages}
            onClick={() => navigate("/app/dossiers?focus=messages")}
          />
          <KpiCard
            testId="kpi-deliveries-today"
            micro="Livraisons"
            primary={deliveriesPrimary}
            secondary={deliveriesHint}
            muted={!primaryDelivery}
            onClick={() => navigate("/app/planning")}
          />
          <KpiCard
            testId="kpi-blocked"
            micro="Blocages"
            primary={numberFR(blockedPrimary)}
            secondary={blockedHint}
            muted={!ov.blocked}
            onClick={() => navigate("/app/dossiers?focus=blocked")}
          />
        </section>

        {/* ─── PRIORITÉS — cœur du dashboard ─── */}
        {priorityList.length > 0 && (
          <section className="mt-24" data-testid="cc-priorities-section">
            <div className="flex items-center justify-between mb-7">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#9CA3AF] dark:text-[#525252]">
                À traiter
              </p>
              <button
                onClick={() => navigate("/app/dossiers")}
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white transition-colors"
              >
                Voir tout
                <ArrowRight size={11} strokeWidth={2.5} />
              </button>
            </div>
            <div className="space-y-2.5">
              {priorityList.map((item) => (
                <PriorityRow
                  key={`prio-${item.id}`}
                  item={item}
                  onOpen={() => openDossier(item.id)}
                  onQuickAction={openQuickAction}
                  testId={`priority-${item.id}`}
                />
              ))}
            </div>
          </section>
        )}

        {/* ─── LIVRAISONS 14 JOURS ─── */}
        {deliveries.length > 0 && (
          <section className="mt-24" data-testid="cc-deliveries-section">
            <div className="flex items-center justify-between mb-7">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#9CA3AF] dark:text-[#525252]">
                À venir
              </p>
              <button
                onClick={() => navigate("/app/planning")}
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white transition-colors"
              >
                Planning
                <ArrowRight size={11} strokeWidth={2.5} />
              </button>
            </div>
            <div className="space-y-2.5">
              {deliveries.map((d) => (
                <DeliveryRow
                  key={`del-${d.id}`}
                  item={d}
                  onOpen={() => openDossier(d.id)}
                  testId={`delivery-${d.id}`}
                />
              ))}
            </div>
          </section>
        )}

        {/* Empty state calme */}
        {priorityList.length === 0 && deliveries.length === 0 && (
          <section
            className="mt-32 rounded-3xl border border-dashed border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] p-16 text-center"
            data-testid="cc-empty"
          >
            <div className="mx-auto h-11 w-11 rounded-full bg-[#F3FCE1] dark:bg-[#1F2A0A] flex items-center justify-center">
              <Sparkles size={17} strokeWidth={1.75} className="text-[#4a6b0f] dark:text-[#C8FF2F]" />
            </div>
            <p className="mt-6 text-[15px] font-semibold text-[#0A0A0A] dark:text-white tracking-tight">
              Tout est calme.
            </p>
          </section>
        )}
      </div>

      {showForm && (
        <DossierForm
          mode="create"
          onClose={() => setShowForm(false)}
          onSaved={(created) => {
            if (created?.id) navigate(`/app/dossier/${created.id}`);
            else load();
          }}
        />
      )}

      {/* Dashboard Express — sheets inline */}
      {quickSheet?.kind === "payment" && (
        <PaymentActionSheet
          mode="confirm"
          dossierId={quickSheet.dossierId}
          initial={quickSheet.paymentSummary}
          onClose={() => setQuickSheet(null)}
          onDone={async () => { setQuickSheet(null); await load(); }}
        />
      )}
      {quickSheet?.kind === "delivery" && (
        <DeliverySlotsSheet
          dossierId={quickSheet.dossierId}
          current={quickSheet.deliveryCurrent}
          defaultLocation={quickSheet.defaultLocation}
          onClose={() => setQuickSheet(null)}
          onSaved={async () => { setQuickSheet(null); await load(); }}
        />
      )}

      {quickLoading && (
        <div className="fixed bottom-6 right-6 z-[9998] bg-[#0A0A0A] text-white text-[12px] px-4 py-2 rounded-full flex items-center gap-2 shadow-lg" data-testid="quick-loading">
          <Loader2 size={12} className="animate-spin" />
          Chargement…
        </div>
      )}
    </div>
  );
};

export default CommandCenter;
